<template>
  <b-card no-body class="card-revenue-budget">
    <b-row class="mx-0">
      <b-col :md="showInfo ? 8 : 12" class="revenue-report-wrapper">
        <div class="chart-header">
          <div class="d-sm-flex align-items-center flex-wrap">
            <h4 class="card-title mb-50 mb-sm-0 mr-2">
              {{ title }}
            </h4>
            <div v-if="hasLimitLabels" class="limit-badges d-flex flex-wrap align-items-center gap-1">
              <b-badge v-if="formattedUpperLimit != null" variant="danger" class="limit-badge limit-badge-upper" pill>
                {{ $t('Upper limit') }}: {{ formattedUpperLimit }}
              </b-badge>
              <b-badge v-if="formattedLowerLimit != null" variant="warning" class="limit-badge limit-badge-lower" pill>
                {{ $t('Lower limit') }}: {{ formattedLowerLimit }}
              </b-badge>
            </div>
          </div>
        </div>

        <!-- chart com scroll horizontal quando muitos pontos -->
        <div v-if="chartScrollMinWidth" class="chart-scroll-wrapper">
          <div class="chart-scroll-inner" :style="{ width: chartScrollMinWidth + 'px' }">
            <e-charts ref="chart" autoresize :options="echartsOption" class="echart-wrapper"
              :style="{ height: '180px', width: chartScrollMinWidth + 'px' }" />
          </div>
        </div>
        <e-charts v-else ref="chart" autoresize :options="echartsOption" class="echart-wrapper"
          :style="{ height: '180px' }" />
      </b-col>

      <b-col v-if="showInfo" md="4" class="budget-wrapper align-self-center">
        <b-avatar class="mb-1" variant="light-info" size="75" style="margin-bottom: 0.5em !important;">
          <feather-icon size="38" :icon="icon" />
        </b-avatar>
        <h4 class="mb-25">
          {{ temperature }} {{ unity }}
        </h4>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BAvatar, BBadge,
} from 'bootstrap-vue'
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/line'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/markLine'
import 'echarts/lib/component/dataZoom'
import 'echarts/lib/component/toolbox'
import 'echarts/lib/component/grid'
import 'echarts/lib/component/axisPointer'

import { $themeColors } from '@themeConfig'
import Ripple from 'vue-ripple-directive'

const GAP_MS = 55 * 60 * 1000 // 55 minutos em milissegundos

export default {
  components: {
    ECharts,
    BCard,
    BButton,
    BRow,
    BCol,
    BAvatar,
    BBadge,
  },
  directives: {
    Ripple,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    temperature: {
      type: String,
      default: '',
    },
    axisX: {
      type: Array,
      required: true,
    },
    axisY: {
      type: Array,
      required: true,
    },
    icon: {
      type: String,
      default: '',
    },
    unity: {
      type: String,
      required: true,
    },
    useDateTimeScale: {
      type: Boolean,
      default: false,
    },
    /** Quando true, formata rótulos do eixo x e tooltip em UTC (para bater com as horas do endpoint de telemetria). */
    useUtcForLabels: {
      type: Boolean,
      default: false,
    },
    /** Linha de referência limite superior (valor no eixo Y). */
    upperLimit: {
      type: Number,
      default: null,
    },
    /** Linha de referência limite inferior (valor no eixo Y). */
    lowerLimit: {
      type: Number,
      default: null,
    },
  },
  computed: {
    showInfo() {
      return !!(this.icon && this.temperature)
    },
    hasLimitLabels() {
      const u = this.upperLimit != null && Number.isFinite(this.upperLimit)
      const l = this.lowerLimit != null && Number.isFinite(this.lowerLimit)
      return u || l
    },
    formattedUpperLimit() {
      if (this.upperLimit == null || !Number.isFinite(this.upperLimit)) return null
      const n = Number(this.upperLimit)
      return Number.isInteger(n) ? String(n) : n.toFixed(2)
    },
    formattedLowerLimit() {
      if (this.lowerLimit == null || !Number.isFinite(this.lowerLimit)) return null
      const n = Number(this.lowerLimit)
      return Number.isInteger(n) ? String(n) : n.toFixed(2)
    },
    /** Número de pontos para decidir scroll horizontal */
    dataPointsCount() {
      if (this.useDateTimeScale && this.axisX && this.axisX.length) {
        return this.axisX.length
      }
      if (this.axisY && this.axisY.length) return this.axisY.length
      return 0
    },
    /** Largura mínima (px) para ativar scroll e evitar sobreposição de labels; null = sem scroll */
    chartScrollMinWidth() {
      const minPointsForScroll = 24
      const pxPerPoint = 32
      if (this.dataPointsCount > minPointsForScroll) {
        return Math.max(400, this.dataPointsCount * pxPerPoint)
      }
      return null
    },
    /** Nome sanitizado do título para uso em arquivos de exportação */
    sanitizedExportFilename() {
      if (!this.title || typeof this.title !== 'string') return 'chart'
      return this.title
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[^a-zA-Z0-9_\s-]/g, '')
        .replace(/\s+/g, '_')
        .slice(0, 80) || 'chart'
    },
    /** Série de dados com nulos inseridos para gaps > 55min */
    seriesData() {
      const y = this.axisY || []
      if (!this.useDateTimeScale) {
        return y.map((v, i) => [this.axisX[i], v])
      }
      const x = this.axisX || []
      const data = []
      for (let i = 0; i < x.length; i++) {
        const currentT = Number(x[i])
        if (i > 0) {
          const prevT = Number(x[i - 1])
          // Quebrar a linha quando o intervalo > 55 minutos
          if (currentT - prevT > GAP_MS) {
            data.push([currentT - 1000, null])
          }
        }
        data.push([currentT, y[i] != null ? y[i] : null])
      }
      return data
    },
    /** markLines para limites superior e inferior */
    markLines() {
      const lines = []
      if (this.upperLimit != null && Number.isFinite(this.upperLimit)) {
        lines.push({
          yAxis: this.upperLimit,
          lineStyle: { color: '#ea5455', type: 'dashed', width: 1.5 },
          label: { show: false },
        })
      }
      if (this.lowerLimit != null && Number.isFinite(this.lowerLimit)) {
        lines.push({
          yAxis: this.lowerLimit,
          lineStyle: { color: '#ff9f43', type: 'dashed', width: 1.5 },
          label: { show: false },
        })
      }
      return lines
    },
    echartsOption() {
      const useUtc = this.useUtcForLabels
      const unity = this.unity

      const formatTime = ts => {
        if (ts == null) return ''
        const d = new Date(ts)
        return useUtc
          ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }

      const color = $themeColors.info || '#00cfe8'

      const yValues = (this.axisY || []).map(v => Number(v)).filter(n => Number.isFinite(n))
      const dataMin = yValues.length ? Math.min(...yValues) : null
      const dataMax = yValues.length ? Math.max(...yValues) : null
      const hasLimits = this.upperLimit != null || this.lowerLimit != null
      let yaxisConfig = {}
      if (hasLimits) {
        const padding = 0.05
        let yMin = dataMin != null ? dataMin : (this.lowerLimit != null ? this.lowerLimit : this.upperLimit)
        let yMax = dataMax != null ? dataMax : (this.upperLimit != null ? this.upperLimit : this.lowerLimit)
        if (this.lowerLimit != null) yMin = Math.min(yMin, this.lowerLimit)
        if (this.upperLimit != null) yMax = Math.max(yMax, this.upperLimit)
        let range = (yMax - yMin) || 1
        if (range <= 0 || !Number.isFinite(range)) range = Math.abs(yMax) || 1
        yaxisConfig = {
          min: Math.floor(yMin - range * padding),
          max: Math.ceil(yMax + range * padding),
        }
      }

      return {
        color: [color],
        animation: false,
        grid: {
          left: 10,
          right: 25,
          top: 40,
          bottom: 15,
          containLabel: true,
        },
        toolbox: {
          show: true,
          right: 10,
          top: -5,
          feature: {
            myExportCsv: {
              show: true,
              title: this.$t ? this.$t('Export to CSV') : 'Export to CSV',
              icon: 'path://M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v6h6v10H6zm2-8h8v2H8v-2zm0 4h5v2H8v-2z',
              iconStyle: { borderColor: '#b9b9c3' },
              emphasis: { iconStyle: { borderColor: color } },
              onclick: () => this.exportToCsv(),
            },
            saveAsImage: {
              show: true,
              name: this.sanitizedExportFilename,
              title: this.$t ? this.$t('Save Image') : 'Save Image',
              icon: 'path://M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z',
              iconStyle: {
                borderColor: '#b9b9c3',
              },
              emphasis: {
                iconStyle: {
                  borderColor: color,
                },
              },
            },
          },
        },
        tooltip: {
          trigger: 'axis',
          confine: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#ebe9f1',
          borderWidth: 1,
          padding: [6, 10],
          textStyle: {
            color: '#5e5873',
            fontSize: 12,
            fontFamily: 'Montserrat, Helvetica, Arial, serif',
          },
          extraCssText: 'box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1); border-radius: 6px;',
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: '#d6dce6',
              type: 'dashed',
              width: 1,
            },
          },
          formatter: params => {
            const p = Array.isArray(params) ? params[0] : params
            if (!p || p.value == null) return ''
            const rawVal = Array.isArray(p.value) ? p.value[1] : p.value
            if (rawVal == null) return ''
            const time = Array.isArray(p.value)
              ? formatTime(p.value[0])
              : (this.axisX && this.axisX[p.dataIndex] != null ? this.axisX[p.dataIndex] : '')
            const displayVal = Number.isInteger(Number(rawVal))
              ? rawVal
              : Number(rawVal).toFixed(2)
            return `
              <div style="font-family: Montserrat, Helvetica, Arial, sans-serif;">
                <div style="font-size: 11px; color: #b9b9c3; margin-bottom: 2px;">${time}</div>
                <div style="display: flex; align-items: center;">
                  <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${color}; margin-right: 6px;"></span>
                  <span style="font-weight: 600; color: #5e5873; font-size: 12px;">${displayVal} <small style="font-weight: normal; color: #b9b9c3;">${unity}</small></span>
                </div>
              </div>
            `
          },
        },
        xAxis: {
          type: this.useDateTimeScale ? 'time' : 'category',
          data: this.useDateTimeScale ? undefined : (this.axisX || []),
          splitLine: { show: false },
          axisLine: {
            show: true,
            lineStyle: { color: '#ebe9f1' },
          },
          axisTick: { show: false },
          axisLabel: {
            fontSize: 11,
            color: '#b9b9c3',
            fontFamily: 'Montserrat, Helvetica, Arial, serif',
            formatter: this.useDateTimeScale ? val => formatTime(val) : undefined,
          },
          splitNumber: 8,
        },
        yAxis: {
          type: 'value',
          name: unity,
          nameTextStyle: {
            color: '#b9b9c3',
            fontSize: 11,
            fontFamily: 'Montserrat, Helvetica, Arial, serif',
            padding: [0, 25, 0, 0],
          },
          scale: true,
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#ebe9f1',
            },
          },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            fontSize: 11,
            color: '#b9b9c3',
            fontFamily: 'Montserrat, Helvetica, Arial, serif',
          },
          ...yaxisConfig,
        },
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: 0,
            filterMode: 'none',
          },
        ],
        series: [
          {
            name: this.title,
            type: 'line',
            data: this.seriesData,
            connectNulls: false,
            smooth: false,
            showSymbol: true,
            symbolSize: 8,
            symbol: 'circle',
            itemStyle: {
              color,
              borderColor: '#ffffff',
              borderWidth: 2,
            },
            lineStyle: {
              color,
              width: 3,
            },
            emphasis: {
              scale: true,
              itemStyle: {
                symbolSize: 10,
                borderColor: '#ffffff',
                borderWidth: 2.5,
              },
            },
            markLine: this.markLines.length > 0
              ? {
                silent: true,
                symbol: ['none', 'none'],
                data: this.markLines,
              }
              : undefined,
          },
        ],
      }
    },
  },
  methods: {
    exportToCsv() {
      if (!this.axisX || !this.axisY) return
      const dateTimeHeader = this.$t ? this.$t('Date / Time') : 'Date / Time'
      let csvContent = 'data:text/csv;charset=utf-8,\uFEFF'
      csvContent += `"${dateTimeHeader}","${this.title}"\n`
      
      const x = this.axisX
      const y = this.axisY
      
      for (let i = 0; i < x.length; i++) {
        let timeLabel = x[i]
        if (this.useDateTimeScale) {
          const date = new Date(Number(timeLabel))
          timeLabel = date.toLocaleString()
        }
        let val = y[i]
        if (val == null) val = ''
        csvContent += `"${timeLabel}",${val}\n`
      }
      
      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute('download', `${this.sanitizedExportFilename}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style scoped>
.card-revenue-budget .revenue-report-wrapper {
  padding: 1.2rem 1rem 0rem 1rem;
  min-width: 0;
}

.chart-header {
  margin-bottom: 0.5rem;
}

.chart-header .card-title {
  margin-bottom: 0 !important;
}

.limit-badges {
  gap: 0.375rem;
}

.limit-badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
}

.limit-badge-upper {
  background-color: #ea5455 !important;
  color: #fff;
}

.limit-badge-lower {
  background-color: #ff9f43 !important;
  color: #fff;
}

.chart-scroll-wrapper {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  min-height: 180px;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 0.25rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;
}

.chart-scroll-wrapper::-webkit-scrollbar {
  height: 6px;
}

.chart-scroll-wrapper::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.chart-scroll-wrapper::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.25);
  border-radius: 4px;
}

.chart-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.4);
}

.chart-scroll-inner {
  height: 180px;
  min-height: 180px;
  flex-shrink: 0;
}

.echart-wrapper {
  width: 100% !important;
  height: 180px !important;
}
</style>
